import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["event", "branchOverrideValue", "setBranchOverrideValue", "shouldShowFollowedBranchByDefault", "flow", "descriptionText", "alertProps"];
import { useCallback, useMemo } from 'react';
import I18n from 'I18n';
import UISelect from 'UIComponents/input/UISelect';
import { DEFAULT_BRANCH_INDEX, isDefaultBranchIndex } from 'automation-ui-action-features/records/recordsUtils';
import StaticBranchName from 'automation-ui-action-features/actions/static-branch/StaticBranchName';
import { getFollowedBranchIndex } from 'automation-ui-log-events/records/LogEvent/utils';
import Action from 'automation-ui-action-features/records/Action';
import { useGetActionOutputsForTargetActionById } from 'automation-ui-action-features/action-outputs/actionOutputHooks';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import { filterTokenProps } from 'foundations-components/utils/filterTokenProps';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BranchOverrideView = _ref => {
  var _options$find;
  let {
      event,
      branchOverrideValue,
      setBranchOverrideValue,
      shouldShowFollowedBranchByDefault = true,
      flow,
      descriptionText,
      alertProps
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    tokenProps
  } = filterTokenProps(rest);
  const getActionOutputsForTargetActionById = useGetActionOutputsForTargetActionById(flow.toHybridFlowInterface());
  const trackEvent = useTrackEvent();
  const action = useMemo(() => new Action(event.action), [event.action]);
  const connection = useMemo(() => action.getConnection(), [action]);
  const getBranchLabelByBranchIndex = useCallback(branchIndex => {
    if (isDefaultBranchIndex(branchIndex)) {
      return connection.getDefaultBranchName() || I18n.text('platformTest.badges.branch.otherwiseDefaultBranchName');
    }
    const branch = connection.getBranches().get(branchIndex);
    const branchName = branch.getBranchName() || I18n.text('platformTest.badges.branch.defaultBranchName', {
      position: branchIndex + 1
    });
    return branchName;
  }, [connection]);
  const makeOption = useCallback((branch, branchIndex) => {
    const labelString = getBranchLabelByBranchIndex(branchIndex);
    const labelNode = action.isStaticBranch() && !isDefaultBranchIndex(branchIndex) ? /*#__PURE__*/_jsx(StaticBranchName, {
      action: action,
      branch: branch,
      branchIndex: branchIndex,
      propertyTypeId: flow.objectTypeId,
      getActionOutputsForTargetActionById: getActionOutputsForTargetActionById
    }) : labelString;
    return {
      text: labelString,
      value: branchIndex,
      disabled: !!branch && !!branch.getConnector() === null,
      dropdownText: labelNode,
      buttonText: labelNode
    };
  }, [action, flow.objectTypeId, getActionOutputsForTargetActionById, getBranchLabelByBranchIndex]);
  const options = useMemo(() => {
    const defaultBranchOption = makeOption(null, DEFAULT_BRANCH_INDEX);
    const branches = connection.getBranches();
    const branchOptions = branches.map(makeOption).toArray();
    if (action.isABTestBranch()) {
      return [...branchOptions];
    }
    return [...branchOptions, defaultBranchOption];
  }, [action, makeOption, connection]);
  return /*#__PURE__*/_jsxs(Alert, Object.assign({}, alertProps, tokenProps, {
    use: "inline",
    display: "block",
    children: [branchOverrideValue !== undefined ? /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "platformTest.badges.branch.overrideSelected",
      options: {
        branchName: (_options$find = options.find(option => option.value === branchOverrideValue)) === null || _options$find === void 0 ? void 0 : _options$find.dropdownText
      }
    }) : descriptionText !== null && descriptionText !== void 0 ? descriptionText : /*#__PURE__*/_jsx(UIHelpIcon, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "platformTest.badges.branch.overrideTooltip"
      }),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "platformTest.badges.branch.overrideDisclaimer"
      })
    }), /*#__PURE__*/_jsx(UISelect, {
      buttonUse: "form-on-dark",
      placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "platformTest.badges.branch.overridePlaceholder"
      }),
      className: "m-top-3",
      options: options,
      onChange: ({
        target: {
          value
        }
      }) => {
        trackEvent(Trackables.CHANGE_BRANCH_OVERRIDE, {
          value
        });
        setBranchOverrideValue(value);
      },
      value: branchOverrideValue !== null && branchOverrideValue !== void 0 ? branchOverrideValue : shouldShowFollowedBranchByDefault ? getFollowedBranchIndex(event) : undefined
    })]
  }));
};
export default BranchOverrideView;